import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { trackServiceView, trackBookingClick } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const DEFAULT_ORDER = 99;

// Static category info - defined outside component for stable reference
const CATEGORY_INFO = {
  'facials': {
    description: 'Rejuvenating facial treatments for all skin types, from signature facials to advanced treatments.',
    image: 'https://images.pexels.com/photos/3212179/pexels-photo-3212179.jpeg',
    order: 1
  },
  'hair-removal': {
    description: 'Professional hair removal services using both traditional and natural methods.',
    image: 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/2zjvfr3a_curated-lifestyle-2vwjeqXZevk-unsplash.jpg',
    order: 2
  },
  'lashes-brows': {
    description: 'Enhance your natural beauty with professional lash and brow treatments.',
    image: 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/c1pxx4gi_amanda-dalbjorn-UbJMy92p8wk-unsplash.jpg',
    order: 3
  },
  'lifestyle': {
    description: 'Wellness treatments to restore balance and promote overall well-being.',
    image: 'https://customer-assets.emergentagent.com/job_sereno-spa/artifacts/t6ommu12_Wellness-Tourism-Why-You-Should-Be-Experiencing-It-e1603999032944.webp',
    order: 4
  },
  'massage': {
    description: 'Therapeutic massage treatments to relieve tension, restore balance, and promote deep relaxation.',
    image: 'https://customer-assets.emergentagent.com/job_fe1c91c4-a8c1-49c7-93bd-706bc696132c/artifacts/pedecleb_ChatGPT%20Image%20Mar%2030%2C%202026%20at%2012_44_43%20PM.png',
    order: 5
  },
  'peels': {
    description: 'Professional chemical peels for skin renewal and rejuvenation.',
    image: 'https://customer-assets.emergentagent.com/job_sereno-wellness/artifacts/73ijqcik_chemical-peels-101-a-complete-beginners-guide-1440x810.jpg',
    order: 6
  }
};

const Services = () => {
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchServices = useCallback(async (retries = 3) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/services`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Transform API data to match component structure
        const categories = Object.keys(data).map(categoryKey => ({
          id: categoryKey,
          name: data[categoryKey].name,
          description: CATEGORY_INFO[categoryKey]?.description || 'Professional spa treatments.',
          image: CATEGORY_INFO[categoryKey]?.image || 'https://images.pexels.com/photos/3212179/pexels-photo-3212179.jpeg',
          order: CATEGORY_INFO[categoryKey]?.order || DEFAULT_ORDER,
          services: data[categoryKey].services.map(service => service.name)
        })).sort((a, b) => a.order - b.order);
        
        setServiceCategories(categories);
        setError(null);
        setLoading(false);
      } else if (retries > 0) {
        setTimeout(() => fetchServices(retries - 1), 2000);
      } else {
        setError('Failed to load services');
        setLoading(false);
      }
    } catch (err) {
      if (retries > 0) {
        setTimeout(() => fetchServices(retries - 1), 2000);
      } else {
        setError('Failed to load services');
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  if (loading) {
    return (
      <div className="services-page">
        <section className="section">
          <div className="container">
            <div className="text-center">Loading services...</div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="services-page">
        <section className="section">
          <div className="container">
            <div className="text-center">
              <p style={{ color: '#e74c3c', marginBottom: '1rem' }}>{error}</p>
              <button onClick={() => { setLoading(true); setError(null); fetchServices(); }} className="btn-primary">
                Try Again
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="services-page">
      <SEOHead 
        title="Spa Services | Facials, Hair Removal, Massage & More | Sereno Spa Livingston Manor"
        description="Explore our comprehensive spa services including professional facials, hair removal, lashes & brows, massage, chemical peels, and wellness treatments at Sereno Spa in Livingston Manor, NY."
        canonical="https://serenodayspa.com/services"
        ogTitle="Professional Spa Services - Sereno Spa"
        ogDescription="Discover our full range of luxury spa treatments. From rejuvenating facials to relaxing massage and wellness services, experience the best in professional spa care."
      />
      <section className="section">
        <div className="container">
          <h1 className="section-title">Our Services</h1>
          <p className="section-subtitle">
            Discover our comprehensive range of spa treatments designed to rejuvenate, restore, and revitalize
          </p>
          
          <div className="services-categories">
            {serviceCategories.map(category => (
              <div key={category.id} className="service-category-card">
                <div className="category-image-container">
                  <img 
                    src={category.image} 
                    alt={category.name}
                    className="category-image"
                  />
                </div>
                <div className="category-content">
                  <h2>{category.name}</h2>
                  <p>{category.description}</p>
                  
                  <div className="services-preview">
                    <h4>Services Include:</h4>
                    <ul>
                      {category.services.slice(0, 3).map((service) => (
                        <li key={service}>{service}</li>
                      ))}
                      {category.services.length > 3 && (
                        <li>...and more</li>
                      )}
                    </ul>
                  </div>
                  
                  <div className="category-actions">
                    <Link 
                      to={`/services/${category.id}`} 
                      className="btn-primary"
                      onClick={() => trackServiceView(category.name)}
                    >
                      Explore {category.name === 'Massage' ? 'Massages' : category.name}
                    </Link>
                    <a 
                      href="https://www.fresha.com/book-now/sereno-spa-xw34dhed/services?lid=2572885&share=true&pId=2492446" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn-secondary"
                      onClick={() => trackBookingClick('Service Category Booking', category.name)}
                    >
                      Book Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;